import { useMemo, useState } from 'react';
import { BlogPostMeta, getBlogManifest } from '@/content/blog/loadPosts';
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { ArrowRight, Newspaper, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  glassCardHoverSx,
  glassCardSx,
  pillLabelSx,
} from '@/components/Marketing/blogStyles';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { usePageMeta } from '@/hooks/usePageMeta';

const ImagePlaceholder = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        aspectRatio: '16 / 9',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.mainColors.primary} 0%, ${theme.palette.mainColors.secondary} 100%)`,
      }}
    >
      <Newspaper
        size={36}
        color={theme.palette.mainColors.white}
        opacity={0.5}
      />
    </Box>
  );
};

const FeaturedPostCard = ({ post }: { post: BlogPostMeta }) => {
  const theme = useTheme();
  return (
    <Box
      component={Link}
      to={`/blog/${post.slug}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        overflow: 'hidden',
        ...glassCardSx(theme),
        ...glassCardHoverSx(theme),
      }}
    >
      <Grid container>
        <Grid item xs={12} md={7}>
          {post.image ? (
            <Box
              component="img"
              src={post.image}
              alt={post.imageAlt ?? post.title}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 260,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <ImagePlaceholder />
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}
            >
              <Chip
                label="Latest"
                size="small"
                color="primary"
                sx={pillLabelSx}
              />
              <Typography variant="body2" color="text.secondary">
                {format(new Date(post.publishedAt), 'MMM d, yyyy')}
              </Typography>
            </Box>
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.2 }}
            >
              {post.title}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              {post.description}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: 600,
                color: theme.palette.mainColors.blue,
              }}
            >
              Read post <ArrowRight size={16} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const PostCard = ({ post }: { post: BlogPostMeta }) => {
  const theme = useTheme();
  return (
    <Box
      component={Link}
      to={`/blog/${post.slug}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        textDecoration: 'none',
        overflow: 'hidden',
        ...glassCardSx(theme),
        ...glassCardHoverSx(theme),
      }}
    >
      {post.image ? (
        <Box
          component="img"
          src={post.image}
          alt={post.imageAlt ?? post.title}
          sx={{ aspectRatio: '16 / 9', width: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImagePlaceholder />
      )}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Chip
            label={post.category}
            size="small"
            variant="outlined"
            sx={pillLabelSx}
          />
          <Typography variant="caption" color="text.disabled">
            {format(new Date(post.publishedAt), 'MMM d, yyyy')} ·{' '}
            {post.readTimeMinutes} min read
          </Typography>
        </Box>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
          {post.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.6, flex: 1 }}
        >
          {post.description}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: theme.palette.mainColors.blue,
          }}
        >
          Read post <ArrowRight size={14} />
        </Box>
      </Box>
    </Box>
  );
};

export default function BlogIndexPage() {
  const theme = useTheme();
  const { posts } = getBlogManifest();
  usePageMeta(
    'Blog',
    'Articles on mortgages, investor markets, and real estate strategy from Unbeatable Loans.'
  );

  const sortedPosts = useMemo(
    () =>
      [...posts].sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      ),
    [posts]
  );
  const [featured, ...rest] = sortedPosts;

  const categories = useMemo(
    () => Array.from(new Set(sortedPosts.map((p) => p.category))),
    [sortedPosts]
  );

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rest.filter((post) => {
      const matchesCategory =
        !activeCategory || post.category === activeCategory;
      const matchesSearch =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.description.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [rest, search, activeCategory]);

  return (
    <MarketingPageShell
      title="Our Latest Articles"
      subtitle="Mortgage savings, market updates, and investor-focused guides."
    >
      {featured && <FeaturedPostCard post={featured} />}

      <Box sx={{ mt: 6 }}>
        <TextField
          fullWidth
          placeholder="Search articles by title or topic…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color={theme.palette.greyShades.grey1} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setActiveCategory(null)}
            color={activeCategory === null ? 'primary' : 'default'}
            variant={activeCategory === null ? 'filled' : 'outlined'}
            sx={pillLabelSx}
          />
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              size="small"
              onClick={() => setActiveCategory(category)}
              color={activeCategory === category ? 'primary' : 'default'}
              variant={activeCategory === category ? 'filled' : 'outlined'}
              sx={pillLabelSx}
            />
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {filtered.length} of {rest.length} articles
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        {filtered.map((post) => (
          <Grid item xs={12} md={6} key={post.slug}>
            <PostCard post={post} />
          </Grid>
        ))}
      </Grid>

      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            No articles match your search. Try a different term or category.
          </Typography>
        </Box>
      )}
    </MarketingPageShell>
  );
}
